import { useEffect, useState, useCallback } from 'react';
import { getImageUrl } from '../../utils/imageUtils';
import { Link, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowRight,
  MapPin,
  ShieldCheck,
  Building2,
  Home,
  TreeDeciduous,
  BadgeCheck,
  Award,
  Users2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const propertyTypeIcons = {
  apartment: Building2,
  villa: Home,
  land: TreeDeciduous,
  plot: TreeDeciduous,
  project: Building2
};

export default function AgencyHome() {
  const { agency } = useOutletContext();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/properties/${agency.slug}?limit=6`);
      setAllProperties(response.data.properties);
      setFeaturedProperties(response.data.properties.filter(p => p.featured));
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  }, [agency.slug]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  const displayProperties = featuredProperties.length > 0 ? featuredProperties : allProperties.slice(0, 3);

  return (
    <div data-testid="agency-home" className="bg-[#FCFDFF]">
      {/* Hero Section - Luxury Editorial Look */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={agency.brand_images?.[0] ? getImageUrl(agency.brand_images[0]) : "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&q=85&w=2000"}
            alt={agency.company_name}
            className="w-full h-full object-cover scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950/90 via-navy-950/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-8">
              {agency.verified && (
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-full text-xs font-bold uppercase tracking-widest backdrop-blur-md">
                  <BadgeCheck className="w-4 h-4" />
                  Elite Partner
                </span>
              )}
              {agency.rera_number && (
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-green-500/10 border border-green-500/20 text-green-400 rounded-full text-xs font-bold uppercase tracking-widest backdrop-blur-md">
                  <ShieldCheck className="w-4 h-4" />
                  RERA Registered
                </span>
              )}
            </div>

            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-heading font-bold text-white leading-[0.9] mb-8 tracking-tight">
              {agency.company_name} <br />
              <span className="text-sky-400 italic">Excellence.</span>
            </h1>

            <p className="text-xl text-white/70 mb-10 max-w-xl leading-relaxed font-light italic">
              {agency.about || 'Curating the world\'s most prestigious addresses for the most discerning clients.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-5">
              <Link to={`/agency/${agency.slug}/properties`}>
                <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white h-14 px-10 text-lg rounded-full shadow-2xl shadow-sky-500/20 transition-all group" data-testid="view-properties-btn">
                  View Collection
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to={`/agency/${agency.slug}/contact`}>
                <Button size="lg" variant="outline" className="h-14 px-10 text-lg border-white/30 text-white hover:bg-white/10 rounded-full backdrop-blur-sm" data-testid="contact-us-btn">
                  Request Private Viewing
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Floating Stat Card */}
        <div className="absolute bottom-12 right-12 hidden xl:block">
          <div className="glass p-8 rounded-3xl border border-white/20 shadow-2xl animate-bounce-slow">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-4xl font-heading font-bold text-navy-900 leading-none">24+</p>
                <p className="text-xs uppercase tracking-widest text-navy-500 mt-2 font-bold leading-tight">Years of <br /> Legacy</p>
              </div>
              <div className="w-px h-12 bg-navy-100/50" />
              <div>
                <p className="text-4xl font-heading font-bold text-navy-900 leading-none">500+</p>
                <p className="text-xs uppercase tracking-widest text-navy-500 mt-2 font-bold leading-tight">Elite <br /> Listings</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured/Latest Properties */}
      <section className="py-20 px-6 bg-white" data-testid="properties-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-sky-500 font-medium text-sm tracking-widest uppercase">
                {featuredProperties.length > 0 ? 'Featured Properties' : 'Our Properties'}
              </span>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-900 mt-2">
                {featuredProperties.length > 0 ? 'Handpicked For You' : 'Latest Listings'}
              </h2>
            </div>
            <Link to={`/agency/${agency.slug}/properties`} className="hidden sm:block">
              <Button variant="outline" className="gap-2" data-testid="view-all-btn">
                View All
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-64 bg-navy-200 rounded-xl mb-4" />
                  <div className="h-6 bg-navy-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-navy-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : displayProperties.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-navy-300" />
              <h3 className="text-lg font-semibold text-navy-900 mb-2">No properties listed yet</h3>
              <p className="text-navy-600">Check back soon for new listings</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {(featuredProperties.length > 0 ? featuredProperties : allProperties.slice(0, 3)).map((property, index) => {
                const TypeIcon = propertyTypeIcons[property.property_type] || Building2;
                return (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="group"
                  >
                    <Link to={`/agency/${agency.slug}/property/${property.id}`}>
                      <div className="property-card h-full flex flex-col" data-testid={`property-card-${property.id}`}>
                        <div className="relative h-80 rounded-[2rem] overflow-hidden mb-6 shadow-2xl shadow-navy-900/10">
                          {property.images?.[0] ? (
                            <img
                              src={getImageUrl(property.images[0])}
                              alt={property.title}
                              className="w-full h-full object-cover property-card-image group-hover:scale-110 transition-transform duration-[1.5s]"
                            />
                          ) : (
                            <div className="w-full h-full bg-navy-50 flex items-center justify-center">
                              <TypeIcon className="w-16 h-16 text-navy-200" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                          <div className="absolute top-5 left-5 flex flex-col gap-2">
                            <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-navy-900 text-[10px] font-bold uppercase tracking-widest rounded-full shadow-sm">
                              {property.property_type}
                            </span>
                          </div>

                          {property.featured && (
                            <div className="absolute top-5 right-5">
                              <span className="flex items-center gap-1.5 px-3 py-1 bg-sky-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg">
                                <Award className="w-3 h-3" />
                                Featured
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="px-2">
                          <h3 className="text-2xl font-heading font-bold text-navy-950 mb-2 group-hover:text-sky-500 transition-colors line-clamp-1">
                            {property.title}
                          </h3>
                          <p className="text-navy-500 flex items-center gap-1.5 mb-6 text-sm italic font-light">
                            <MapPin className="w-3.5 h-3.5 shrink-0 text-sky-400" />
                            {property.location_area}, {property.location_city}
                          </p>

                          <div className="flex items-center justify-between pt-6 border-t border-navy-50">
                            <div className="flex flex-col">
                              <span className="text-xs uppercase tracking-widest text-navy-400 font-bold mb-0.5">Price</span>
                              <span className="text-2xl font-bold text-navy-950 tracking-tight">
                                {formatPrice(property.price)}
                              </span>
                            </div>
                            <div className="flex flex-col text-right">
                              <span className="text-xs uppercase tracking-widest text-navy-400 font-bold mb-0.5">Area</span>
                              <span className="text-lg font-medium text-navy-700">
                                {property.size} <span className="text-xs font-bold text-navy-400">{property.size_unit}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="text-center mt-8 sm:hidden">
            <Link to={`/agency/${agency.slug}/properties`}>
              <Button variant="outline" className="gap-2">
                View All Properties
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-navy-50" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sky-500 font-medium text-sm tracking-widest uppercase">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-900 mt-2">
              Trusted Real Estate Partner
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'RERA Verified',
                description: 'All our properties and operations are RERA compliant, ensuring complete transparency.',
                icon: ShieldCheck
              },
              {
                title: 'Direct from Developer',
                description: 'No middlemen or brokers. Deal directly with us for the best prices and authentic information.',
                icon: Building2
              },
              {
                title: 'Expert Guidance',
                description: 'Our experienced team guides you through every step of your property journey.',
                icon: Home
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 text-center shadow-soft"
              >
                <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-sky-500" />
                </div>
                <h3 className="text-xl font-semibold text-navy-900 mb-3">{item.title}</h3>
                <p className="text-navy-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Gallery (Showcase more than 1 image) */}
      {agency.brand_images?.length > 1 && (
        <section className="py-20 px-6 bg-white overflow-hidden" data-testid="brand-gallery-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sky-500 font-medium text-sm tracking-widest uppercase">The Experience</span>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-900 mt-2">
                Our World
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {agency.brand_images.slice(1).map((url, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative rounded-2xl overflow-hidden shadow-lg ${i === 0 ? 'col-span-2 row-span-2 h-full' : 'h-48'
                    }`}
                >
                  <img src={getImageUrl(url)} alt={`Showcase ${i}`} className="w-full h-full object-cover" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-6 bg-navy-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
            Ready to Find Your Dream Property?
          </h2>
          <p className="text-navy-300 mb-8 text-lg">
            Get in touch with us today and let us help you find the perfect property.
          </p>
          <Link to={`/agency/${agency.slug}/contact`}>
            <Button size="lg" className="bg-sky-500 hover:bg-sky-600 h-12 px-8 text-base" data-testid="get-in-touch-btn">
              Get In Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
